import SanityImage from '@otovo/shared/components/Sanity/SanityImage';

import PortableText from '@otovo/shared/components/Sanity/PortableTextComponents';
import { useGlobalConfig } from '@otovo/shared/hooks/configContext';
import { Otovo$Locale } from '@otovo/shared/types/otovoweb';
import { Sanity$TextImageCTABlock } from '@otovo/shared/types/sanityTypes';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Badge from '../../v2/Badge/Badge';
import CTAButton from '../../v2/CTAButton';
import { routeToHref } from '../routeToHrefUtil';

const Sticker = dynamic(() => import('../../Sanity/Sticker'));

type Props = {
  content: Sanity$TextImageCTABlock;
};

const TextImageCTABlock = ({ content }: Props) => {
  const { locale } = useRouter() as { locale: Otovo$Locale };
  const config = useGlobalConfig();

  function renderCta() {
    if (!content.cta) {
      return null;
    }
    return (
      <CTAButton
        href={routeToHref(content.cta.route, config.BU_CONFIG.market, locale)}
        className="mt-8"
      >
        {content.cta.title}
      </CTAButton>
    );
  }
  if (content.type === 'default') {
    return (
      <section className="color-scheme-light relative mx-4 my-16 max-w-8xl justify-center gap-0 rounded-md bg-surface p-8 md:mx-6 md:my-20 lg:mx-16 lg:my-24 lg:grid lg:grid-cols-12 lg:p-16 2xl:mx-auto">
        <div className="col-span-5 prose mb-12 lg:mb-0 prose-ul:p-0">
          <h3 className="mb-6 text-2xl font-semibold text-balance break-words text-heading md:text-3xl lg:mr-16 lg:text-4xl">
            {content.heading}
          </h3>
          <PortableText value={content.body} />
          {renderCta()}
        </div>
        <div className="col-span-6 col-start-7 w-full overflow-hidden">
          <SanityImage
            className="aspect-square w-full rounded-xl sm:aspect-auto"
            equalizeImageHeight
            image={content.image}
            sizes="(max-width: 767px) 80vw, 480px"
          />
        </div>
      </section>
    );
  }

  return (
    <section className="color-scheme-blue relative mx-4 my-16 max-w-8xl justify-center gap-6 md:mx-6 md:my-20 lg:mx-16 lg:my-24 lg:grid lg:grid-cols-12 2xl:mx-auto">
      <div className="col-span-5 mb-6 flex items-center rounded-xl bg-blue-60 p-8 lg:mb-0 lg:p-16 prose-ul:p-0">
        <div>
          {!!content.badge && <Badge badge={content.badge} />}

          <h3 className="mb-3 text-2xl font-medium break-words text-heading md:mb-6">
            {content.heading}
          </h3>
          <div className="prose">
            <PortableText value={content.body} />
          </div>
          {renderCta()}
        </div>
      </div>
      <div className="relative col-span-7 w-full overflow-hidden">
        {content.sticker && <Sticker sticker={content.sticker} />}
        <SanityImage
          className="h-full w-full rounded-xl object-cover"
          maxHeightFull
          image={content.image}
          sizes="(max-width: 767px) 80vw, 800px"
        />
      </div>
    </section>
  );
};

export default TextImageCTABlock;
